import type { InviteResponse, PortalRole, PortalTeam, TeamUser } from '@central/api';
import {
  Card,
  StatusChip,
  formatAgo,
  formatDate,
  InviteLink,
  isLastOwner,
  mfaLabel,
} from '@central/ui';
import { Alert, Button, Group, Modal, Select, Switch, Table, TextInput } from '@mantine/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { keys, useApi } from '../api';
import { DataTable, Empty, QueryState } from '../components/parts';
import { errorMessage } from '../lib/errors';
import { muted } from '../lib/labels';
import { useCanWrite } from '../lib/me';

const roles: PortalRole[] = ['Owner', 'Member', 'Viewer'];

/** The customer's portal users and pending invites (Owner/Admin change them). */
export function PortalUsers({ customerId }: { customerId: string }) {
  const api = useApi();
  const team = useQuery({
    queryKey: keys.customerUsers(customerId),
    queryFn: () => api.customers.users(customerId),
  });
  const users = team.data?.users ?? [];
  const invites = team.data?.invites ?? [];
  return (
    <Card padded={false}>
      <div style={{ padding: '16px 20px' }}>
        <h2 style={{ margin: 0, fontSize: 16 }}>Portal users</h2>
        <div style={{ fontSize: 13, ...muted }}>
          The last enabled owner can’t be disabled or demoted
        </div>
      </div>
      <QueryState isPending={team.isPending} error={team.error}>
        {() =>
          users.length + invites.length === 0 ? (
            <Empty>No portal users yet. Invite the customer’s first Owner.</Empty>
          ) : (
            <DataTable
              label="Portal users"
              minWidth={760}
              head={['Name', 'Email', 'Role', 'MFA', 'Last sign-in', 'Enabled']}
            >
              {users.map((u) => (
                <UserRow key={u.id} customerId={customerId} u={u} team={team.data!} />
              ))}
              {invites.map((i) => (
                <Table.Tr key={i.id}>
                  <Table.Td style={muted}>–</Table.Td>
                  <Table.Td>{i.email}</Table.Td>
                  <Table.Td>{i.role}</Table.Td>
                  <Table.Td>
                    <StatusChip tone="unk">Pending setup</StatusChip>
                  </Table.Td>
                  <Table.Td style={muted}>Invite expires {formatDate(i.expiresAt)}</Table.Td>
                  <Table.Td />
                </Table.Tr>
              ))}
            </DataTable>
          )
        }
      </QueryState>
    </Card>
  );
}

function UserRow({ customerId, u, team }: { customerId: string; u: TeamUser; team: PortalTeam }) {
  const api = useApi();
  const queryClient = useQueryClient();
  const canWrite = useCanWrite();
  const update = useMutation({
    mutationFn: (b: { role?: PortalRole; enabled?: boolean }) =>
      api.customers.updateUser(customerId, u.id, b),
    onSuccess: (next) =>
      queryClient.setQueryData<PortalTeam>(keys.customerUsers(customerId), (t) =>
        t ? { ...t, users: t.users.map((x) => (x.id === next.id ? next : x)) } : t,
      ),
  });
  const locked = isLastOwner(u, team.users);
  const disabled = !canWrite || locked || update.isPending;
  return (
    <>
      <Table.Tr>
        <Table.Td style={{ fontWeight: 600 }}>{u.name}</Table.Td>
        <Table.Td>{u.email}</Table.Td>
        <Table.Td>
          <Select
            aria-label={`Role of ${u.name}`}
            size="xs"
            w={120}
            data={roles}
            value={u.role}
            allowDeselect={false}
            disabled={disabled}
            onChange={(v) => v && v !== u.role && update.mutate({ role: v as PortalRole })}
          />
        </Table.Td>
        <Table.Td>{mfaLabel(u)}</Table.Td>
        <Table.Td style={muted}>{u.lastSignInAt ? formatAgo(u.lastSignInAt) : 'Never'}</Table.Td>
        <Table.Td>
          <Switch
            aria-label={`${u.name} enabled`}
            checked={u.enabled}
            disabled={disabled}
            onChange={(ev) => update.mutate({ enabled: ev.currentTarget.checked })}
          />
        </Table.Td>
      </Table.Tr>
      {update.error && (
        <Table.Tr>
          <Table.Td colSpan={6}>
            <Alert color="red">{errorMessage(update.error)}</Alert>
          </Table.Td>
        </Table.Tr>
      )}
    </>
  );
}

/** Invite a portal user; the link is emailed to them and also shown once. */
export function InviteModal({
  customerId,
  opened,
  onClose,
  hasOwner,
}: {
  customerId: string;
  opened: boolean;
  onClose: () => void;
  /** Without an enabled Owner the invite defaults to Owner. */
  hasOwner: boolean;
}) {
  const api = useApi();
  const queryClient = useQueryClient();
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<PortalRole>();
  const [emailError, setEmailError] = useState<string>();
  // Shown once: held only in this component, never in the query cache.
  const [sent, setSent] = useState<InviteResponse>();
  const chosen = role ?? (hasOwner ? 'Viewer' : 'Owner');
  const invite = useMutation({
    mutationFn: () => api.customers.invite(customerId, { email: email.trim(), role: chosen }),
    onSuccess: async (r) => {
      setSent(r);
      // The Portal users tab label counts the same rows as the table.
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: keys.customerUsers(customerId) }),
        queryClient.invalidateQueries({ queryKey: keys.customerCounts(customerId) }),
      ]);
    },
    onError: (e) => {
      const f = (e as { fields?: Record<string, string> }).fields;
      if (f?.email) setEmailError('Enter a valid email address.');
    },
  });
  const close = () => {
    setEmail('');
    setRole(undefined);
    setSent(undefined);
    setEmailError(undefined);
    invite.reset();
    onClose();
  };
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return setEmailError('Enter their email address.');
    setEmailError(undefined);
    invite.mutate();
  };
  return (
    <Modal opened={opened} onClose={close} title="Invite portal user">
      {sent ? (
        <InviteLink
          email={sent.invite.email}
          inviteUrl={sent.inviteUrl}
          expiresAt={sent.invite.expiresAt}
          emailed={sent.emailed}
          onDone={close}
        />
      ) : (
        <form
          onSubmit={submit}
          noValidate
          style={{ display: 'flex', flexDirection: 'column', gap: 14 }}
        >
          <TextInput
            label="Email"
            type="email"
            data-autofocus
            value={email}
            error={emailError}
            onChange={(e) => setEmail(e.currentTarget.value)}
          />
          <Select
            label="Role"
            data={roles}
            value={chosen}
            allowDeselect={false}
            onChange={(v) => v && setRole(v as PortalRole)}
          />
          {invite.error && !emailError && <Alert color="red">{errorMessage(invite.error)}</Alert>}
          <Group justify="flex-end">
            <Button variant="default" onClick={close}>
              Cancel
            </Button>
            <Button type="submit" color="dark" loading={invite.isPending}>
              Create invite
            </Button>
          </Group>
        </form>
      )}
    </Modal>
  );
}
